import { applianceHidesCommerceMarker } from '@/services/appliance-commerce-surface'

export interface FeaturedTemplate {
  id: string
  route: string
  label: string
  description: string
  category: string
  keywords: string[]
  blocks: number
  gradient: string
  /** Static thumbnail path — e.g. "/thumbnails/coffee.png" */
  thumbnail?: string
  /** Reference sites from design-reference-sites.md */
  refs?: string[]
  /** v2 validation score (0-100) */
  v2Score?: number
}

/**
 * Categories
 *
 * E-Commerce — Stores, shops, marketplaces
 * SaaS & Portfolio — Landing pages, dashboards, portfolios
 * Content — Blogs, content platforms
 * Productivity — Trackers, dashboards, admin tools
 */
export const TEMPLATE_CATEGORIES = [
  { value: "all", label: "All" },
  { value: "ecommerce", label: "E-Commerce" },
  { value: "saas", label: "SaaS & Portfolio" },
  { value: "content", label: "Content" },
  { value: "productivity", label: "Productivity" },
] as const

/**
 * 21개 v2 템플릿 (schemaVersion 3.0)
 * 2026-03-26 전체 new3 앱 등록
 */
/**
 * Every featured template this build knows about, appliance filtering aside.
 * Read `FEATURED_TEMPLATES` below — not this — unless you specifically need
 * the pre-filter inventory.
 */
const FEATURED_TEMPLATES_ALL: FeaturedTemplate[] = [
  // Parked 2026-08-21: unverified starter templates moved to
  // apps/cogo-starter/public/apps-backup-20260821. Product surface is empty
  // until DS → ontology library → UI block admission.
]

/**
 * The featured gallery as this build actually offers it.
 *
 * THE single exclusion point for this inventory. Every consumer reads THIS
 * export — the `/templates` + showroom pages (pages/TemplatesPage.tsx,
 * pages/TemplateDetailPublicPage.tsx), the homepage prompt→template matcher
 * (pages/homepage/hooks/use-app-generator.ts), `HOMEPAGE_TEMPLATES` below,
 * and — the case a nav/route filter can never reach —
 * `features/cogo-xyz/cogo-xyz-template-gallery.ts`, which backs the
 * REGISTERED, INVOCABLE WebMCP tools `cogo.studio.list_cogo_templates`
 * (readOnly: an agent can enumerate the gallery) and
 * `cogo.studio.remix_cogo_template` (an agent can instantiate one into a
 * real project). Hiding `/studio/commerce` from the nav rail did nothing to
 * either of those. The 21 unverified starter rows were parked 2026-08-21.
 *
 * Evaluated once at module load, exactly like `STUDIO_ROUTE_REGISTRY`:
 * `VITE_*` is a Vite build-time constant, so a one-time filter is equivalent
 * to and cheaper than each consumer re-checking. Under the default (cloud)
 * distribution this is a no-op — identical to `FEATURED_TEMPLATES_ALL`.
 */
export const FEATURED_TEMPLATES: FeaturedTemplate[] = FEATURED_TEMPLATES_ALL.filter(
  (t) => !applianceHidesCommerceMarker(t.category),
)

/**
 * Curated 6 templates for homepage — 2 per major category for visual diversity.
 * Full gallery remains in FEATURED_TEMPLATES for /templates page.
 */
export const HOMEPAGE_TEMPLATE_IDS = [
] as const

export const HOMEPAGE_TEMPLATES = FEATURED_TEMPLATES.filter(
  (t) => (HOMEPAGE_TEMPLATE_IDS as readonly string[]).includes(t.id)
)
